import React, { useMemo, useState } from "react";
import { PDFDocument } from "pdf-lib";

function baseName(filename = "document.pdf") {
  return filename.replace(/\.[^/.]+$/, "");
}

export default function MergePdf() {
  const [files, setFiles] = useState([]); // File[]
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { url, name }
  const [error, setError] = useState("");

  const canMerge = useMemo(
    () => files.length >= 2 && !loading,
    [files, loading]
  );

  const cleanup = () => {
    if (result?.url) URL.revokeObjectURL(result.url);
    setResult(null);
  };

  const onPick = (fileList) => {
    cleanup();
    setError("");
    const arr = Array.from(fileList || []).filter(
      (f) =>
        f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );
    setFiles(arr);
  };

  const move = (index, dir) => {
    cleanup();
    setError("");
    setFiles((prev) => {
      const next = [...prev];
      const newIndex = index + dir;
      if (newIndex < 0 || newIndex >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[newIndex];
      next[newIndex] = tmp;
      return next;
    });
  };

  const removeAt = (index) => {
    cleanup();
    setError("");
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const merge = async () => {
    if (files.length < 2) return;

    setLoading(true);
    setError("");
    cleanup();

    try {
      const mergedPdf = await PDFDocument.create();

      for (const f of files) {
        const bytes = await f.arrayBuffer();
        const doc = await PDFDocument.load(bytes);
        const copiedPages = await mergedPdf.copyPages(
          doc,
          doc.getPageIndices()
        );
        copiedPages.forEach((p) => mergedPdf.addPage(p));
      }

      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([mergedBytes], { type: "application/pdf" });

      const outName =
        files.length === 2
          ? `${baseName(files[0].name)}+${baseName(files[1].name)}-fusion.pdf`
          : `pdf-fusion-${files.length}-fichiers.pdf`;

      const url = URL.createObjectURL(blob);
      setResult({ url, name: outName });
    } catch (e) {
      console.error(e);
      setError(
        "Impossible de fusionner ces PDFs (fichier corrompu ou protégé)."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={{ padding: 16 }}>
      <h2>Fusionner des PDF</h2>
      <small>Tout se fait dans ton navigateur (aucun envoi)</small>

      <input
        type="file"
        accept="application/pdf,.pdf"
        multiple
        disabled={loading}
        onChange={(e) => onPick(e.target.files)}
      />

      {files.length > 0 && (
        <div style={{ maxWidth: 650, margin: "14px auto", textAlign: "left" }}>
          <p style={{ color: "#666" }}>
            Ordre de fusion (tu peux réorganiser) :
          </p>

          <div style={{ display: "grid", gap: 10 }}>
            {files.map((f, i) => (
              <div
                key={`${f.name}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: 10,
                  border: "1px solid #e6e6e6",
                  borderRadius: 10,
                  background: "#fff",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b
                    style={{
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {i + 1}. {f.name}
                  </b>
                  <small style={{ color: "#666" }}>
                    {(f.size / 1024 / 1024).toFixed(2)} Mo
                  </small>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => move(i, -1)}
                    disabled={loading || i === 0}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => move(i, +1)}
                    disabled={loading || i === files.length - 1}
                  >
                    ↓
                  </button>
                  <button onClick={() => removeAt(i)} disabled={loading}>
                    Retirer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 12,
          marginTop: 12,
          flexWrap: "wrap",
        }}
      >
        <button onClick={merge} disabled={!canMerge} style={{ minWidth: 220 }}>
          {loading ? "Fusion..." : "Fusionner en un seul PDF"}
        </button>

        {files.length > 0 && (
          <button
            onClick={() => {
              cleanup();
              setFiles([]);
              setError("");
            }}
            disabled={loading}
            style={{ minWidth: 160 }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {error && (
        <p style={{ textAlign: "center", marginTop: 12, color: "#b00020" }}>
          {error}
        </p>
      )}

      {result?.url && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <a href={result.url} download={result.name}>
            Télécharger le PDF fusionné
          </a>
        </div>
      )}
    </section>
  );
}
